import { useState } from "react";
import { useLocation } from "react-router-dom";
import "./PatientSessionDetails.scss";
import Spinner from "../../../components/Spinner/Spinner";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import { BackArrow } from "../../../assets/icons-components/Icons";
import DownloadOptionsModal from "../../../components/DownloadOptionsModal/DownloadOptionsModal";
import ProcessingPopup from "../../../components/ProcessingPopup/ProcessingPopup";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Buttons from "../../../components/Buttons/Buttons";
import usePatientForm from "../Patient/usePatientForm";
import PatientNote from "../../../components/PatientNote/PatientNote";
import { safeParseSessionStorage } from "../../../utils/storage";
import usePatientSessionDetails, {
  formatDate,
  formatDateOnly,
  formatSecondsToHMS,
  noT,
} from "./usePatientSessionDetails";
import ReferenceDataModal from "./ReferenceDataModal";
import ProfileTab from "./ProfileTab";
import SessionsTab from "./SessionsTab";
import AcquisitionSettingsTab from "./AcquisitionSettingsTab";
import SurveyTab from "./SurveyTab";

export default function PatientSessionDetails() {
  const {
    page,
    rowsPerPage,
    rows,
    totalRows,
    isLoading,
    isReferenceLoading,
    fetchError,
    calendarOpen,
    processingSessionId,
    downloadPopupOpen,
    downloadPopupTitle,
    downloadPopupLoading,
    downloadOptionsOpen,
    downloadOptionsRow,
    isDownloadingCsv,
    isDownloadingProcessedCsv,
    downloadProgress,
    referenceModal,
    referenceData,
    deleteSessionDialog,
    selectedDate,
    highlightedDates,
    Name,
    DeviceId,
    deletePatientDialog,
    isDeletingPatient,
    setCalendarOpen,
    setReferenceModal,
    setDownloadOptionsOpen,
    handleDateChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleDelete,
    confirmDelete,
    cancelDelete,
    handleView,
    handleTable,
    handleRefresh,
    handleMainTableDownload,
    handleDownloadCSVUrl,
    handleDownloadProcessedCSVUrl,
    handleReferenceDownload,
    handleCloseProcessingPopup,
    handleDeletePatientClick,
    confirmDeletePatient,
    cancelDeletePatient,
  } = usePatientSessionDetails();

  const location = useLocation();
  // Returning from the survey detail view (see SurveyTab's handleView /
  // SurveyResultDetailPage's handleBack) reopens on the tab it left from,
  // instead of always resetting to Sessions.
  const [activeTab, setActiveTab] = useState(location?.state?.activeTab ?? 0);
  const [isEditingPatientInfo, setIsEditingPatientInfo] = useState(false);
  const [isEditingAcquisition, setIsEditingAcquisition] = useState(false);
  const patientFormProps = usePatientForm();
  const rowData = safeParseSessionStorage("rowData");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Every tab switch should land on a clean view — drop any unsaved edit
    // state instead of leaving it to leak into the next visit.
    setIsEditingPatientInfo(false);
    setIsEditingAcquisition(false);
    patientFormProps.resetForm();
  };

  return (
    <div className="patient-session-details">
      {isReferenceLoading && (
        <div className="loader-container">
          <div className="loader">
            <Spinner />
          </div>
        </div>
      )}

      {/* Main Header showing back arrow and Patient Name / Device Info */}
      <div className="patient-session-details_headers">
        <div className="patient-session-details_headers_left">
          <div className="patient-session-details_headers_left_arrow">
            <Buttons
              icon={<BackArrow />}
              ariaLabel="Back to Patients"
              onClick={handleTable}
              style={{
                border: "none",
                padding: 0,
                backgroundColor: "transparent",
              }}
            />
            <h3>
              {Name || "-"} - [{DeviceId || "-"}]
            </h3>
          </div>
        </div>
      </div>

      {/* Tab bar layout */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          margin: "10px 12px 20px 12px",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="patient details tabs"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "#104581",
              height: "3px",
            },
            "& .MuiTab-root": {
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "none",
              color: "#6b6a6a",
              minWidth: "120px",
            },
            "& .MuiTab-root.Mui-selected": { color: "#104581" },
          }}
        >
          <Tab label="Sessions" />
          <Tab label="Notes" />
          <Tab label="Profile" />
          <Tab label="Data Settings" />
          <Tab label="Survey" />
        </Tabs>
      </Box>

      {/* Tab Panel contents */}
      <div style={{ padding: "0 12px" }}>
        {activeTab === 2 && (
          <ProfileTab
            isEditingPatientInfo={isEditingPatientInfo}
            setIsEditingPatientInfo={setIsEditingPatientInfo}
            patientFormProps={patientFormProps}
            isDeletingPatient={isDeletingPatient}
            onDeletePatientClick={handleDeletePatientClick}
          />
        )}

        {activeTab === 0 && (
          <SessionsTab
            calendarOpen={calendarOpen}
            setCalendarOpen={setCalendarOpen}
            selectedDate={selectedDate}
            handleDateChange={handleDateChange}
            highlightedDates={highlightedDates}
            handleRefresh={handleRefresh}
            rows={rows}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRows}
            processingSessionId={processingSessionId}
            noT={noT}
            formatDateOnly={formatDateOnly}
            formatSecondsToHMS={formatSecondsToHMS}
            onView={handleView}
            onDownload={handleMainTableDownload}
            onDelete={handleDelete}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            isLoading={isLoading}
            fetchError={fetchError}
          />
        )}

        {activeTab === 1 && rowData?.id && (
          <div className="tab-panel-content">
            <PatientNote patientId={rowData.id} />
          </div>
        )}

        {activeTab === 3 && (
          <AcquisitionSettingsTab
            isEditingAcquisition={isEditingAcquisition}
            setIsEditingAcquisition={setIsEditingAcquisition}
            patientFormProps={patientFormProps}
          />
        )}

        {activeTab === 4 && rowData?.id && <SurveyTab patientId={rowData.id} />}
      </div>

      {/* Popups & Dialogs */}
      {downloadPopupOpen && (
        <ProcessingPopup
          title={downloadPopupTitle}
          loading={downloadPopupLoading}
          progress={downloadProgress}
          onClose={handleCloseProcessingPopup}
        />
      )}

      {deleteSessionDialog.open && (
        <ConfirmDialog
          open={deleteSessionDialog.open}
          onClose={cancelDelete}
          title="Delete Session"
          severity="error"
          confirmText="DELETE"
          content={
            <span>
              Are you sure you want to delete session{" "}
              <strong>#{deleteSessionDialog.target}</strong>?
            </span>
          }
          buttonText="Yes, Delete"
          additionalButtonText="Cancel"
          onButtonClick={confirmDelete}
          onAdditionalButtonClick={cancelDelete}
        />
      )}

      {deletePatientDialog.open && (
        <ConfirmDialog
          open={deletePatientDialog.open}
          onClose={cancelDeletePatient}
          title="Delete Patient"
          severity="error"
          confirmText="DELETE"
          content={
            <span>
              Are you sure you want to delete patient{" "}
              <strong>{Name || "this patient"}</strong>? This action cannot be
              undone.
            </span>
          }
          buttonText="Yes, Delete"
          additionalButtonText="Cancel"
          onButtonClick={confirmDeletePatient}
          onAdditionalButtonClick={cancelDeletePatient}
        />
      )}

      {referenceModal && (
        <ReferenceDataModal
          referenceData={referenceData}
          processingSessionId={processingSessionId}
          formatDate={formatDate}
          formatDateOnly={formatDateOnly}
          onView={handleView}
          onDownload={handleReferenceDownload}
          onClose={() => setReferenceModal(false)}
        />
      )}

      {downloadOptionsOpen && downloadOptionsRow && (
        <DownloadOptionsModal
          row={downloadOptionsRow}
          onDownloadCsv={handleDownloadCSVUrl}
          onDownloadProcessedCsv={handleDownloadProcessedCSVUrl}
          onCancel={() => setDownloadOptionsOpen(false)}
          isDownloadingCsv={isDownloadingCsv}
          isDownloadingProcessedCsv={isDownloadingProcessedCsv}
          downloadProgress={downloadProgress}
        />
      )}
    </div>
  );
}
